import Core                       from '@/core/Core';
import CharacteristicGroup        from '@/domains/CharacteristicGroups/CharacteristicGroup';
import CharacteristicGroupsDAO    from '@/domains/CharacteristicGroups/CharacteristicGroupsDAO';
import ContextMenuItem            from "@/lib/components/VContextMenu/ContextMenuItem";

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class CharacteristicGroupsService {

	// бэк не принимает id, по идее этот метод и не нужен
	static async get(id: number): Promise<CharacteristicGroup | null> {
		const result = await this.getList({ id });
		return result[0] ?? null;
	}

	static async getListPaginated(
		params?: Parameters<typeof CharacteristicGroupsDAO.getList>[0] & {
			pagination?: RpcDataPagination;
			sort?: RpcDataSort;
		}
	): Promise<{ data: CharacteristicGroup[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await CharacteristicGroupsDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, CharacteristicGroup.fromServer),
			pagination: response.meta.pagination,
		};
	}

	static async getList(
		params?: Parameters<typeof CharacteristicGroupsDAO.getList>[0]
	): Promise<CharacteristicGroup[]> {
		const result = await this.getListPaginated(params);
		return result.data;
	}

	static async add(characteristicGroup: CharacteristicGroup): Promise<boolean> {
		const response = await CharacteristicGroupsDAO.add(characteristicGroup);

		if (response.ok) {
			Core.showNotification.success('Группа характеристик добавлена');
		}

		return response.ok;
	}

	static async update(characteristicGroup: CharacteristicGroup): Promise<boolean> {
		const response = await CharacteristicGroupsDAO.update(characteristicGroup);

		if (response.ok) {
			Core.showNotification.success('Группа характеристик обновлена');
		}

		return response.ok;
	}

	static async delete(id: number): Promise<boolean> {
		const response = await CharacteristicGroupsDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Группа характеристик удалена');
		}

		return response.ok;
	}

	static async deleteWithConfirmation(group: CharacteristicGroup): Promise<boolean> {
		const confirmed = await Core.dialogs.confirm(
			'Удалить эту группу характеристик?',
			`Группа: ${group.name}`,
		);

		if (confirmed === true) {
			return await this.delete(group.id);
		} else {
			return false;
		}
	}

	static async changeSort(
		movedElementId: number,
		targetElementId: number,
		insertAfter: boolean,
	): Promise<boolean> {
		const response = await CharacteristicGroupsDAO.changeSort(movedElementId, targetElementId, insertAfter);

		if (response.ok) {
			Core.showNotification.success('Порядок групп обновлён');
		}

		return response.ok;
	}

	/**
	 * Показывает карточку группы характеристик. null — создание новой группы.
	 * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
	 */
	static showCharacteristicGroupCard(group: CharacteristicGroup | null): Promise<boolean> {
		return Core.dialogs.CharacteristicGroupCard.showAsPromise({ entity: group });
	}

	// ============================================================
	// Контекстное меню
	// ============================================================

	/**
	 * Пункты контекстного меню группы характеристик для грида/реестра.
	 * Передаётся в VDataGridV2 пропом :context-menu — грид сам рисует
	 * кебаб-колонку и меню по правому клику, и обновляет список после
	 * действия, только если callback пункта вернул true.
	 *
	 * Геттер, а не статическое свойство — для единообразия с другими
	 * сервисами, где меню зависит от Core.currentPartner.
	 *
	 * callback возвращает промис — грид дождётся завершения перед refresh.
	 */
	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(group: CharacteristicGroup) => { return this.showCharacteristicGroupCard(group); },
				null,
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(group: CharacteristicGroup) => { return this.deleteWithConfirmation(group); },
			),
		];
	}
}

export default CharacteristicGroupsService;